import { useMemo, useRef, useState } from 'react';
import { fetchCardCatalog } from '../api/client';
import { useAsync } from '../api/useCatalog';
import type { CardMetadata, CardType, CreatureKind } from '../types/catalog';
import { isUnit } from '../types/catalog';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { CardTile } from '../components/CardTile';
import { CardModal } from '../components/CardModal';
import { FilterBar, type Filters, EMPTY_FILTERS } from '../components/FilterBar';

/**
 * Embaralha uma cópia da lista (Fisher–Yates). Não muta a original: `data.cards`
 * vem do catálogo e é reusada pelos contadores e pelos filtros.
 */
function shuffle<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return out;
}

/** Aplica os filtros ativos sobre a lista de cartas. */
function applyFilters(cards: CardMetadata[], f: Filters): CardMetadata[] {
  const query = f.search.trim().toLowerCase();
  return cards.filter((card) => {
    if (f.types.size > 0 && !f.types.has(card.type)) return false;

    if (f.kinds.size > 0) {
      // Filtro por tag só faz sentido para unidades; cartas sem tag são excluídas.
      if (!isUnit(card)) return false;
      const has = card.kinds.some((k) => f.kinds.has(k));
      if (!has) return false;
    }

    if (query) {
      const haystack = `${card.name} ${card.description}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

/** Conta quantas cartas existem de cada tipo (para os contadores dos chips). */
function countByType(cards: CardMetadata[]): Record<CardType, number> {
  const counts: Record<CardType, number> = { unit: 0, spell: 0, trap: 0 };
  for (const c of cards) counts[c.type]++;
  return counts;
}

export function CardsPage(): React.JSX.Element {
  const { data, loading, error, reload } = useAsync(fetchCardCatalog);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<CardMetadata | null>(null);

  // Ordem aleatória, sorteada uma única vez por carregamento do catálogo: se
  // embaralhasse a cada render, a grade se reorganizaria a cada tecla digitada
  // na busca ou clique num filtro. O resultado é guardado num ref (e não apenas
  // no useMemo, que o React pode descartar) para que a ordem seja garantida —
  // reembaralhar durante a filtragem seria desorientador.
  const shuffledRef = useRef<{ source: readonly CardMetadata[]; order: CardMetadata[] } | null>(null);
  const cards = useMemo(() => {
    const source = data?.cards ?? [];
    if (shuffledRef.current?.source !== source) {
      shuffledRef.current = { source, order: shuffle(source) };
    }
    return shuffledRef.current.order;
  }, [data]);
  const typeCounts = useMemo(() => countByType(cards), [cards]);
  const filtered = useMemo(() => applyFilters(cards, filters), [cards, filters]);

  // Tags presentes no catálogo (para não oferecer filtros sem resultados).
  const availableKinds = useMemo(() => {
    const set = new Set<CreatureKind>();
    for (const c of cards) if (isUnit(c)) for (const k of c.kinds) set.add(k);
    return set;
  }, [cards]);

  return (
    <>
      <div className="page-head">
        <h1>Cartas</h1>
        <p>
          Todas as cartas do jogo, divididas em <strong>unidades</strong>, <strong>feitiços</strong> e{' '}
          <strong>armadilhas</strong>. Use os filtros para encontrar cartas por tipo, grupo ou nome.
          {data && <> Catálogo v{data.version}.</>}
        </p>
      </div>

      <AsyncBoundary loading={loading} error={error} onRetry={reload}>
        <FilterBar
          filters={filters}
          onChange={setFilters}
          typeCounts={typeCounts}
          availableKinds={availableKinds}
        />

        <p className="result-count">
          {filtered.length === cards.length
            ? `${cards.length} cartas`
            : `${filtered.length} de ${cards.length} cartas`}
        </p>

        {filtered.length === 0 ? (
          <div className="state">Nenhuma carta corresponde aos filtros.</div>
        ) : (
          <div className="card-grid">
            {filtered.map((card) => (
              <CardTile key={card.cardId} card={card} onClick={() => setSelected(card)} />
            ))}
          </div>
        )}
      </AsyncBoundary>

      {selected && <CardModal card={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
